(() => {
  if (window.PonPonLocation) return;
  const node = document.getElementById('pp-retailer-config');
  if (!node) return;
  const config = JSON.parse(node.textContent);
  const key = 'ponpon.retailer.location.v1';
  const ipKey = 'ponpon.retailer.ip.v1';
  let ipPromise;
  let retailerPromise;
  const listeners = new Set();
  function point(value) {
    if (value?.lat === '' || value?.lng === '' || value?.lat == null || value?.lng == null) return null;
    const lat = Number(value.lat), lng = Number(value.lng);
    return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 85 && Math.abs(lng) <= 180 ? { lat, lng } : null;
  }
  function miles(a, b) {
    const rad = n => n * Math.PI / 180;
    const h = Math.sin(rad(b.lat - a.lat) / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lng - a.lng) / 2) ** 2;
    return 3958.7613 * 2 * Math.asin(Math.sqrt(Math.min(1, h)));
  }
  function read(storageKey) {
    try {
      const value = JSON.parse(sessionStorage.getItem(storageKey));
      if (!value || !Number.isFinite(value.expires) || value.expires < Date.now()) return null;
      if (value.source === 'unknown') return value;
      return point(value) ? value : null;
    } catch { return null; }
  }
  function write(storageKey, value) {
    try { sessionStorage.setItem(storageKey, JSON.stringify(value)); } catch { /* Storage is optional. */ }
  }
  let manual = read(key);
  function save(value) {
    const coords = point(value);
    if (!coords || !['zip', 'device'].includes(value.source)) return;
    manual = { ...coords, source: value.source, zip: /^\d{5}$/.test(value.zip || '') ? value.zip : '', expires: Date.now() + (value.source === 'device' ? 30 * 60 * 1000 : 24 * 60 * 60 * 1000) };
    write(key, manual);
    listeners.forEach(callback => callback(manual));
  }
  function clear() {
    manual = null;
    try { sessionStorage.removeItem(key); } catch { /* Storage is optional. */ }
    listeners.forEach(callback => callback(null));
  }
  function saved() {
    if (manual?.expires > Date.now()) return manual;
    manual = null;
    return null;
  }
  function testMode() {
    if (!config.preview) return 'auto';
    const requested = new URL(location.href).searchParams.get('pp_location_test') || config.testMode;
    return ['near', 'far', 'unknown'].includes(requested) ? requested : 'auto';
  }
  async function resolve() {
    // Explicit ZIP/device choices always take precedence, including over preview simulations.
    const chosen = saved();
    if (chosen) return chosen;
    const mode = testMode();
    if (mode === 'near') return { lat: 34.10, lng: -118.07, source: 'test' };
    if (mode === 'far') return { lat: 40.71, lng: -74.01, source: 'test' };
    if (mode === 'unknown') return null;
    if (!config.ipEnabled || !config.endpoint) return null;
    const cached = read(ipKey);
    if (cached) return cached.source === 'ip' ? cached : null;
    if (!ipPromise) ipPromise = (async () => {
      try {
        const endpoint = new URL(config.endpoint);
        if (endpoint.protocol !== 'https:') return null;
        const response = await fetch(endpoint.href, { signal: AbortSignal.timeout(4500), credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer' });
        if (!response.ok) throw new Error('Location unavailable');
        const data = await response.json();
        const coords = data.source === 'ip' && point(data);
        const result = coords ? { ...coords, source: 'ip', expires: Date.now() + 60 * 60 * 1000 } : null;
        write(ipKey, result || { source: 'unknown', expires: Date.now() + 5 * 60 * 1000 });
        return result;
      } catch { return null; }
    })();
    const estimate = await ipPromise;
    return saved() || estimate;
  }
  async function retailers() {
    if (!retailerPromise) retailerPromise = (async () => {
      const records = new Map();
      let url = new URL(config.root || '/', location.origin);
      url.searchParams.set('section_id', 'pp-retailer-data');
      const seen = new Set();
      while (url) {
        if (seen.has(url.href)) throw new Error('Repeated retailer page');
        seen.add(url.href);
        const response = await fetch(url.href, { signal: AbortSignal.timeout(8000), credentials: 'same-origin' });
        if (!response.ok) throw new Error('Retailers unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!doc.querySelector('[data-retailer-page]')) throw new Error('Retailers missing');
        for (const record of doc.querySelectorAll('[data-retailer-point]')) {
          const coords = point(record.dataset);
          if (coords) records.set(record.dataset.id, coords);
        }
        const next = doc.querySelector('[data-next]')?.getAttribute('href');
        if (!next) break;
        const page = new URL(next, location.origin);
        if (page.origin !== location.origin) throw new Error('Invalid retailer page');
        for (const [name, value] of page.searchParams) url.searchParams.set(name, value);
        url.searchParams.set('section_id', 'pp-retailer-data');
      }
      return [...records.values()];
    })();
    return retailerPromise;
  }
  function state(origin, stores) {
    if (!point(origin) || !Array.isArray(stores) || !stores.length) return 'unknown';
    return stores.some(store => point(store) && miles(origin, store) <= (Number(config.radius) || 25)) ? 'near' : 'far';
  }
  function decorateLinks(root = document) {
    const mode = testMode();
    if (mode === 'auto') return;
    for (const link of root.querySelectorAll('[data-locator-link]')) {
      const url = new URL(link.href);
      url.searchParams.set('pp_location_test', mode);
      link.href = url.href;
    }
  }
  window.PonPonLocation = { config, point, miles, save, clear, saved, resolve, retailers, state };
  class FindInStore extends HTMLElement {
    connectedCallback() {
      this.revision = (this.revision || 0) + 1;
      this.update = () => this.refresh();
      listeners.add(this.update);
      decorateLinks(this);
      this.refresh();
    }
    disconnectedCallback() { this.revision++; listeners.delete(this.update); }
    async refresh() {
      const revision = ++this.revision;
      let result = 'unknown';
      try {
        const [origin, stores] = await Promise.all([resolve(), retailers()]);
        result = state(origin, stores);
      } catch { /* A plain link remains usable if either service fails. */ }
      if (revision !== this.revision || !this.isConnected) return;
      this.dataset.state = result;
      this.hidden = result === 'far';
    }
  }
  if (!customElements.get('ponpon-find-in-store')) customElements.define('ponpon-find-in-store', FindInStore);
  decorateLinks();
  document.addEventListener('shopify:section:load', event => decorateLinks(event.target));
})();
